/**
 * Billing (`/billing`, TMMRSL-0046, MM-D-105): the reseller's card on file, collection of a
 * negative ledger balance from that card (TMMRSL-0080), and the charges made to it (TMMAPI-0098),
 * under the attention banners. Every role sees the page; only an OWNER may change the card or the
 * collection authorization (tmm-api still answers 403). After a save or a removal the card, the
 * charges and the authorization reload; nothing is optimistic.
 */
import { useCallback } from 'react';
import { Box, Stack } from '@mui/material';
import { collectionPresentation, resellerBillingApi } from '@/api/resellerBilling';
import { PageHeader } from '@/components/PageHeader';
import { useRemote } from '@/components/useRemote';
import { BillingAttention } from './BillingAttention';
import { CardOnFileSection } from './CardOnFileSection';
import { ChargesSection } from './ChargesSection';
import { CollectionSection } from './CollectionSection';
import { isOpenCharge } from './openCharges';
import { useBillingData } from './useBillingData';

const loadCollection = () => resellerBillingApi.getCollectionAuthorization();

export function BillingScreen() {
  const { card, charges, version, reloadAll } = useBillingData();
  const collection = useRemote(loadCollection);
  const hasOpenCharges = charges.data?.some(isOpenCharge) === true;
  const collectionAuthorized =
    collection.data !== null && collectionPresentation(collection.data) === 'authorized';
  const reloadCollection = collection.reload;
  const reload = useCallback(() => {
    reloadAll();
    reloadCollection();
  }, [reloadAll, reloadCollection]);

  return (
    <Box>
      <PageHeader id="billing" />
      <Stack spacing={2.5}>
        <BillingAttention key={version} showLink={false} />
        <CardOnFileSection
          card={card}
          hasOpenCharges={hasOpenCharges}
          collectionAuthorized={collectionAuthorized}
          onChanged={reload}
        />
        <CollectionSection
          state={collection}
          cardOnFile={card.status === 'ready' ? card.data !== null : null}
        />
        <ChargesSection charges={charges} hasOpenCharges={hasOpenCharges} onChanged={reload} />
      </Stack>
    </Box>
  );
}
